/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardTabContent.tsx
 * FILE_ID: UI-KANBAN-CARD-TAB-CONTENT-001
 * PURPOSE: Card-tab frame — layout menu, then the hideable catalog.
 * WHY: Leftover sections must honor the same order/hide prefs as the spine.
 * NOTES FOR FUTURE AI: Order/hide lives in useCardTabLayout. Hide omits
 *   chrome. Mail and notes stay on the card.
 */
import type { CardTabContentProps } from './cardTabContentTypes'
import { CardTabLayoutMenu } from './CardTabLayoutMenu'
import { CardTabOrderedBlocks } from './CardTabOrderedBlocks'
import { renderCardTabLeftovers } from './CardTabLeftovers'
import { useCardTabLayout } from './useCardTabLayout'
import { useDrawerSectionPrefs } from './drawerSectionPrefs'

export type { CardTabContentProps } from './cardTabContentTypes'

export function CardTabContent(props: CardTabContentProps) {
  const {
    card,
    collabCard,
    api,
    boardId,
    members,
    currentUserId,
    emailAdapter,
    editing,
    data,
    openEmailTab,
    cardBodyFindRef,
    safeActiveTab,
  } = props
  const sectionPrefs = useDrawerSectionPrefs()
  const cardTabLayout = useCardTabLayout(boardId)
  const leftovers = renderCardTabLeftovers(props, sectionPrefs)

  return (
    <div
      ref={cardBodyFindRef}
      role="tabpanel"
      id="card-detail-tabpane-card"
      hidden={safeActiveTab !== 'card'}
      className={[
        'flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-5 pb-16 space-y-6',
        safeActiveTab !== 'card' ? 'hidden' : '',
      ].join(' ')}
    >
      <CardTabLayoutMenu
        layout={cardTabLayout.layout}
        move={cardTabLayout.move}
        setHidden={cardTabLayout.setHidden}
        reset={cardTabLayout.reset}
      />
      <CardTabOrderedBlocks
        visible={cardTabLayout.visible}
        leftovers={leftovers}
        card={card}
        collabCard={collabCard}
        api={api}
        boardId={boardId}
        members={members}
        currentUserId={currentUserId}
        emailAdapter={emailAdapter}
        editing={editing}
        data={data}
        openEmailTab={openEmailTab}
      />
      {data.mirrorParentId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-snug text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
          Personal copy of a team card — its schedule is yours, but
          labels, members &amp; chat are shared with the team.
        </div>
      )}
    </div>
  )
}
